/**
 * Constants for Voice DTMF component
 */

import { logger } from './logger';

/**
 * Validate and parse environment variable as number
 *
 * Ensures env var is valid number within optional min/max bounds.
 * Returns defaultValue if invalid or out of range.
 *
 * @param key - Environment variable key
 * @param defaultValue - Default value if invalid
 * @param min - Optional minimum value (inclusive)
 * @param max - Optional maximum value (inclusive)
 * @returns Validated number or default value
 */
function getEnvNumber(
  key: string,
  defaultValue: number,
  min?: number,
  max?: number
): number {
  const value = Number(import.meta.env[key]);

  if (isNaN(value)) {
    logger.warn(`Invalid env var ${key}, using default: ${defaultValue}`);
    return defaultValue;
  }

  if (min !== undefined && value < min) {
    logger.warn(
      `Env var ${key}=${value} below min ${min}, using default: ${defaultValue}`
    );
    return defaultValue;
  }

  if (max !== undefined && value > max) {
    logger.warn(
      `Env var ${key}=${value} above max ${max}, using default: ${defaultValue}`
    );
    return defaultValue;
  }

  return value;
}

/**
 * Mock delays for testing (in milliseconds)
 *
 * TODO: Review and adjust these delays after end-to-end integration testing
 * These values are currently estimates and may need tuning based on:
 * - Real BFF response times
 * - SSE event delivery latency
 * - Actual DTMF digit capture timing
 * - Network conditions in production environment
 */
export const MOCK_DELAYS = {
  SECURE_MODE_ACTIVATION: 500,
  CARD_ENTRY_START: 1000,
  PAYMENT_PROCESSING: 2000,
  DIGIT_ENTRY: 1500,
} as const;

/**
 * UX Delays
 * Timing constants for UI polish and user experience
 */
export const UX_DELAYS = {
  /** Minimum loading screen display time to prevent flashing (ms) */
  MINIMUM_LOADING_SCREEN: 1500,
  /** Copy success notification display time (ms) */
  COPY_SUCCESS_NOTIFICATION: 2000,
  /** Auto-dismiss toast notification (ms) */
  AUTO_DISMISS_TOAST: 3000,
} as const;

/**
 * Default customer name for saved cards
 * TODO: Fetch from customer service/API
 */
export const DEFAULT_CARDHOLDER_NAME = 'Customer'; // Generic placeholder

/**
 * Default call reference code for Sycurio secure mode
 * Used as fallback when callReference prop is not provided
 * In production, this comes from the BFF /sessions/{sessionId} response
 */
export const DEFAULT_CALL_REFERENCE = '*#10438';

/**
 * API Configuration
 * Controls HTTP client behavior and timeouts
 */
export const API_CONFIG = {
  TIMEOUT_MS: getEnvNumber('VITE_API_TIMEOUT_MS', 30000, 1000, 120000),
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1000,
  RETRY_MAX_DELAY_MS: 10000,
  RETRY_BACKOFF_MULTIPLIER: 2,
} as const;

/**
 * SSE Configuration
 * Controls Server-Sent Events connection and reconnection behavior
 */
export const SSE_CONFIG = {
  RECONNECT_ENABLED: import.meta.env.VITE_SSE_RECONNECT_ENABLED !== 'false',
  MAX_RECONNECT_ATTEMPTS: getEnvNumber(
    'VITE_SSE_MAX_RECONNECT_ATTEMPTS',
    3,
    0,
    50
  ),
  INITIAL_RECONNECT_DELAY_MS: 1000,
  MAX_RECONNECT_DELAY_MS: 32000,
  BACKOFF_MULTIPLIER: 2,
} as const;

/**
 * App Configuration
 * Global app settings and feature flags
 */
export const APP_CONFIG = {
  USE_MOCK_DATA: import.meta.env.VITE_USE_MOCK_DATA === 'true',
  DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true',
  ENV: import.meta.env.VITE_ENV || 'dev',
} as const;

/**
 * Application Routes
 */
export const ROUTES = {
  PAYMENT_SUCCESS: '/payment-success',
} as const;

/**
 * Session Configuration
 * Controls authentication session behavior and expiry warnings
 */
export const SESSION_CONFIG = {
  TTL_MINUTES: 30,
  EXPIRY_WARNING_MINUTES: 5, // Show warning when 5 minutes remaining
  TTL_MS: 30 * 60 * 1000, // 30 minutes in milliseconds
} as const;
